import {
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import {
	UntypedFormBuilder,
	UntypedFormGroup,
	Validators,
} from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Store } from '@ngrx/store';

import { AddressConstants } from '../../../../modules/address/address.constants';
import { BenefitSummary, BenefitType } from '../../models/benefits.model';
import {
	PositionBenefit,
	PositionBenefitForm,
	PositionRegistration,
	PositionReqBenefit,
	PositionRequest,
} from '../../models/positions.model';
import {
	editPositionRegStart,
	fetchOrgBenefitsStart,
	fetchUFCitiesClear,
	fetchUFCitiesStart,
	fetchPositionRegStart,
	createPositionStart,
	restoreFormReset,
} from '../../state/organizations.actions';
import {
	selectBenefits,
	selectCities,
	selectPosition,
	selectRestoreForm,
} from '../../state/organizations.selector';

import { BenefitModalComponent } from './benefit-modal/benefit-modal.component';

@Component({
	selector: 'app-position-alert-dialog',
	template: `
		<app-dialog
			modalTitle="Atenção"
			titleButton="Ok"
			[asAlert]="true"
			(dialogClose)="dialogRef.close()"
			(dialogSave)="dialogRef.close(true)"
		>
			<p class="lh-md">
				As alterações não afetarão os beneficiários que possuem esse cargo. Você
				deverá fazer a alteração do cargo direto no beneficiário
			</p>
		</app-dialog>
	`,
})
export class PositionAlertDialogComponent {
	constructor(public dialogRef: MatDialogRef<PositionAlertDialogComponent>) {}
}

@Component({
	selector: 'app-position-registration',
	styleUrls: ['./position-registration.component.scss'],
	templateUrl: './position-registration.component.html',
})
export class PositionRegistrationComponent implements OnInit, OnDestroy {
	@ViewChild('cpick') cpick!: ElementRef;
	@ViewChild('dpick') dpick!: ElementRef;
	@ViewChild('hpick') hpick!: ElementRef; // Conjugated picker

	ansOptions = [
		{ label: 'Sim', value: true },
		{ label: 'Não', value: false },
	];
	auxStateForm!: boolean;
	benefits: BenefitSummary[] = [];
	cities: string[] = [];
	conjugated: PositionBenefitForm[] = [
		{
			allow_upgrade: false,
			base_id: '',
			benefit_id: '',
			benefit_name: '',
			benefit_partner: '',
			is_upgrade: false,
			upgrades: [],
		},
	];
	dental: PositionBenefitForm[] = [
		{
			allow_upgrade: false,
			base_id: '',
			benefit_id: '',
			benefit_name: '',
			benefit_partner: '',
			is_upgrade: false,
			upgrades: [],
		},
	];
	dialogRef!: MatDialogRef<
		BenefitModalComponent | PositionAlertDialogComponent
	>;
	filteredBenefits: BenefitSummary[] = [];
	form: UntypedFormGroup;
	health: PositionBenefitForm[] = [
		{
			allow_upgrade: false,
			base_id: '',
			benefit_id: '',
			benefit_name: '',
			benefit_partner: '',
			is_upgrade: false,
			upgrades: [],
		},
	];
	initialEditingBenefits: { base_id: string; benefit_id: string }[] = [];
	inputId: string = String(Math.random());
	loading: boolean = true;

	orgId: string = '';
	positionId: string = '';

	showHint: boolean = false;

	uf = AddressConstants.ufLabels;

	private readonly unsubscribe$: Subject<void> = new Subject();

	constructor(
		private readonly fb: UntypedFormBuilder,
		private readonly activatedRoute: ActivatedRoute,
		private readonly dialog: MatDialog,
		private readonly store: Store,
	) {
		this.form = this.fb.group({
			city: [{ disabled: true, value: null }],
			max_wage: [null],
			min_wage: [null],
			name: [null, [Validators.required]],
			state: [null],
		});
	}

	filterBaseBenefits(type: BenefitType, base: string[]): void {
		this.filteredBenefits = this.benefits.filter(
			(b) => b.type === type && !base.includes(b.id),
		);
	}

	filterUpgradeBenefits(
		type: BenefitType,
		base: string,
		partner: string,
		upgrades: string[],
	): void {
		this.filteredBenefits = this.benefits.filter(
			(b) =>
				b.type === type &&
				b.partner === partner &&
				b.id !== base &&
				!upgrades.includes(b.id),
		);
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
		this.store.dispatch(fetchUFCitiesClear());
		this.store.dispatch(restoreFormReset());
	}

	ngOnInit(): void {
		this.activatedRoute.params.subscribe(({ id, cId }) => {
			this.orgId = id;
			this.store.dispatch(fetchOrgBenefitsStart({ id: this.orgId }));
			if (!cId) {
				this.auxStateForm = true;
				this.loading = false;
			} else {
				this.positionId = cId;
				this.store.dispatch(fetchPositionRegStart({ id, positionId: cId }));
				this.store
					.select(selectPosition)
					.pipe(takeUntil(this.unsubscribe$))
					.subscribe((p) => {
						if (p) {
							this.form = this.fb.group({
								city: [{ disabled: true, value: p.city }],
								max_wage: [p.max_wage ? p.max_wage.replace('.', ',') : null],
								min_wage: [p.min_wage ? p.min_wage.replace('.', ',') : null],
								name: [p.name, [Validators.required]],
								state: [p.state],
							});
							this.processEditingPosition(p);
							this.auxStateForm = false;
							this.loading = false;
						}
					});
			}
		});
		this.store
			.select(selectBenefits)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((benefits) => {
				if (benefits) {
					this.benefits = benefits;
				}
			});
		this.store
			.select(selectCities)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((cities) => {
				if (cities.length) {
					this.form.get('city')!.enable();
				}
				this.cities = cities;
			});
	}

	onAddBase(
		benefitListType: 'conjugated' | 'dental' | 'health' = 'health',
	): void {
		const base: PositionBenefitForm = {
			allow_upgrade: false,
			base_id: '',
			benefit_id: '',
			benefit_name: '',
			benefit_partner: '',
			is_upgrade: false,
			upgrades: [],
		};

		if (benefitListType === 'dental') {
			this.dental.push(base);
		} else if (benefitListType === 'health') {
			this.health.push(base);
		} else if (benefitListType === 'conjugated') {
			this.conjugated.push(base);
		}
	}

	onAddUpgrade(
		idx: number,
		benefitListType: 'conjugated' | 'dental' | 'health' = 'health',
	): void {
		const currentList =
			benefitListType === 'health'
				? this.health
				: benefitListType === 'dental'
					? this.dental
					: this.conjugated;

		const upgrade: PositionBenefitForm = {
			base_id: currentList[idx].benefit_id,
			benefit_id: '',
			benefit_name: '',
			benefit_partner: '',
			is_upgrade: true,
			upgrades: [],
		};

		currentList[idx].upgrades.push(upgrade);
	}

	onAllowUpgradeChange(
		idx: number,
		benefitListType: 'conjugated' | 'dental' | 'health' = 'health',
	): void {
		this.form.markAsTouched();
		const currentList =
			benefitListType === 'health'
				? this.health
				: benefitListType === 'dental'
					? this.dental
					: this.conjugated;
		const result = currentList[idx].allow_upgrade;

		if (!result) {
			currentList[idx].upgrades = [];
		} else {
			const upgrade: PositionBenefitForm = {
				base_id: currentList[idx].benefit_id,
				benefit_id: '',
				benefit_name: '',
				benefit_partner: '',
				is_upgrade: true,
				upgrades: [],
			};
			currentList[idx].upgrades.push(upgrade);
		}
	}

	onRemoveUpgrade(
		bIdx: number,
		uIdx: number,
		benefitListType: 'conjugated' | 'dental' | 'health' = 'health',
	): void {
		const currentList =
			benefitListType === 'health'
				? this.health
				: benefitListType === 'dental'
					? this.dental
					: this.conjugated;
		currentList[bIdx].upgrades.splice(uIdx, 1);
	}

	onSubmit(): void {
		const request = this.processForm(this.positionId ? true : false);

		if (request) {
			this.store
				.select(selectRestoreForm)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((restore) => {
					if (restore) {
						this.updateValidation();
						this.loading = false;
					}
				});
			if (this.positionId) {
				this.dialogRef = this.dialog.open(PositionAlertDialogComponent, {
					disableClose: true,
				});
				this.dialogRef.afterClosed().subscribe((save: boolean) => {
					if (save) {
						this.store.dispatch(
							editPositionRegStart({
								id: this.orgId,
								positionId: this.positionId,
								request,
							}),
						);
						this.loading = true;
					}
				});
			} else {
				this.store.dispatch(createPositionStart({ id: this.orgId, request }));
				this.loading = true;
			}
		}
	}

	onUFChange(id: number): void {
		this.form.get('city')!.setValue(null);
		this.form.get('city')!.disable();
		this.auxStateForm = true;
		if (id !== -1) {
			this.store.dispatch(fetchUFCitiesStart({ id }));
		}
	}

	openBaseConjugatedBenefitModal(index: number): void {
		this.form.markAsTouched();
		const base = this.conjugated.map((b) => b.benefit_id);
		this.filterBaseBenefits('CONJUGATED', base);
		this.dialogRef = this.dialog.open(BenefitModalComponent, {
			data: { benefits: this.filteredBenefits, type: 'conjugated' },
			maxWidth: undefined,
		});
		this.dialogRef.afterClosed().subscribe((result) => {
			if (result !== undefined) {
				this.conjugated[index].allow_upgrade = false;
				this.conjugated[index].upgrades = [];
				if (result === null) {
					this.conjugated[index].base_id = '';
					this.conjugated[index].benefit_id = '';
					this.conjugated[index].benefit_name = '';
					this.conjugated[index].benefit_partner = '';
				} else {
					this.conjugated[index].base_id = result.id;
					this.conjugated[index].benefit_id = result.id;
					this.conjugated[index].benefit_name = result.name;
					this.conjugated[index].benefit_partner = result.partner;
				}
			}
		});
	}

	openBaseDentalBenefitModal(index: number): void {
		this.form.markAsTouched();
		const base = this.dental.map((b) => b.benefit_id);
		this.filterBaseBenefits('DENTAL', base);
		this.dialogRef = this.dialog.open(BenefitModalComponent, {
			data: { benefits: this.filteredBenefits, type: 'dental' },
			maxWidth: undefined,
		});
		this.dialogRef.afterClosed().subscribe((result) => {
			if (result !== undefined) {
				this.dental[index].allow_upgrade = false;
				this.dental[index].upgrades = [];
				if (result === null) {
					this.dental[index].base_id = '';
					this.dental[index].benefit_id = '';
					this.dental[index].benefit_name = '';
					this.dental[index].benefit_partner = '';
				} else {
					this.dental[index].base_id = result.id;
					this.dental[index].benefit_id = result.id;
					this.dental[index].benefit_name = result.name;
					this.dental[index].benefit_partner = result.partner;
				}
			}
		});
	}

	openBaseHealthBenefitModal(index: number): void {
		this.form.markAsTouched();
		const base = this.health.map((b) => b.benefit_id);
		this.filterBaseBenefits('HEALTH', base);
		this.dialogRef = this.dialog.open(BenefitModalComponent, {
			data: { benefits: this.filteredBenefits, type: 'health' },
			maxWidth: undefined,
		});
		this.dialogRef.afterClosed().subscribe((result) => {
			if (result !== undefined) {
				this.health[index].allow_upgrade = false;
				this.health[index].upgrades = [];
				if (result === null) {
					this.health[index].base_id = '';
					this.health[index].benefit_id = '';
					this.health[index].benefit_name = '';
					this.health[index].benefit_partner = '';
				} else {
					this.health[index].base_id = result.id;
					this.health[index].benefit_id = result.id;
					this.health[index].benefit_name = result.name;
					this.health[index].benefit_partner = result.partner;
				}
			}
		});
	}

	openUpgradeBenefitModal(
		index: number,
		baseIndex: number,
		base: string,
		partner: string,
		benefitListType: 'conjugated' | 'dental' | 'health' = 'health',
	): void {
		this.form.markAsTouched();
		const currentList =
			benefitListType === 'health'
				? this.health
				: benefitListType === 'dental'
					? this.dental
					: this.conjugated;
		const upgrades = currentList[baseIndex].upgrades.map((u) => u.benefit_id);

		let benefitModalType: BenefitType = 'HEALTH';

		if (benefitListType === 'dental') {
			benefitModalType = 'DENTAL';
		} else if (benefitListType === 'conjugated') {
			benefitModalType = 'CONJUGATED';
		}

		this.filterUpgradeBenefits(benefitModalType, base, partner, upgrades);
		this.dialogRef = this.dialog.open(BenefitModalComponent, {
			data: {
				benefits: this.filteredBenefits,
				type: benefitListType,
			},
			maxWidth: undefined,
		});
		this.dialogRef.afterClosed().subscribe((result) => {
			if (result !== undefined && result !== null) {
				const targetUpgradeList = currentList[baseIndex].upgrades;
				targetUpgradeList[index].benefit_id = result.id;
				targetUpgradeList[index].benefit_name = result.name;
				targetUpgradeList[index].benefit_partner = result.partner;
			} else if (result === null) {
				const targetUpgradeList = currentList[baseIndex].upgrades;
				targetUpgradeList[index].benefit_id = '';
				targetUpgradeList[index].benefit_name = '';
				targetUpgradeList[index].benefit_partner = '';
			}
		});
	}

	processBenefitsForms(): PositionReqBenefit[] {
		const benefits: PositionReqBenefit[] = [];

		const processList = (list: PositionBenefitForm[]) => {
			list.forEach((b) => {
				let index = 1;

				if (b.benefit_id.length) {
					benefits.push({
						base_id: b.base_id,
						benefits_id: b.benefit_id,
						index,
						is_upgrade: b.is_upgrade,
					});
					b.upgrades.forEach((u) => {
						if (u.benefit_id.length) {
							index++;
							benefits.push({
								base_id: u.base_id,
								benefits_id: u.benefit_id,
								index,
								is_upgrade: u.is_upgrade,
							});
						}
					});
				}
			});
		};
		processList(this.health);
		processList(this.dental);
		processList(this.conjugated);

		return benefits;
	}

	processEditingPosition(p: PositionRegistration): void {
		const mapBenefits = (
			benefitList: PositionBenefit[] | undefined,
		): PositionBenefitForm[] => {
			if (!benefitList) return [];

			return benefitList.map((b, idx) => {
				const base: PositionBenefitForm = this.processPositionBenefit(
					b,
					false,
					benefitList[idx].upgrades?.length || 0,
				);
				b.upgrades?.forEach((u) =>
					base.upgrades.push(this.processPositionBenefit(u, true, 0)),
				);

				return base;
			});
		};
		this.health = mapBenefits(p.health);
		this.dental = mapBenefits(p.dental);
		this.conjugated = mapBenefits((p as any).conjugated); // Support optional conjugated

		if (!this.health.length) {
			this.onAddBase('health');
		}

		if (!this.dental.length) {
			this.onAddBase('dental');
		}

		if (!this.conjugated.length) {
			this.onAddBase('conjugated');
		}
	}

	processForm(editing: boolean = false): PositionRequest | null {
		if (this.form.invalid) {
			return null;
		}

		const form = this.form.value;
		const benefits_connect = this.processBenefitsForms();
		const benefits_disconnect:
			| { base_id: string; benefit_id: string }[]
			| undefined = editing
			? this.verifyEditDelBenefits(benefits_connect)
			: undefined;

		return {
			benefits_connect,
			benefits_disconnect,
			city: form.city,
			max_wage: form.max_wage
				? Number(String(form.max_wage).replace(',', '.'))
				: null,
			min_wage: form.min_wage
				? Number(String(form.min_wage).replace(',', '.'))
				: null,
			name: form.name,
			state: form.state,
		};
	}

	processPositionBenefit(
		pb: PositionBenefit,
		is_upgrade: boolean,
		uCount: number,
	): PositionBenefitForm {
		this.initialEditingBenefits.push({
			base_id: pb.base_id,
			benefit_id: pb.benefits_id,
		});

		return {
			allow_upgrade: is_upgrade ? undefined : uCount > 0,
			base_id: pb.base_id,
			benefit_id: pb.benefits_id,
			benefit_name: pb.name,
			benefit_partner: pb.partner_name,
			is_upgrade: false,
			upgrades: [],
		};
	}

	shouldDisableSaveBtn(): boolean {
		return this.form.invalid || this.form.untouched;
	}

	updateValidation(): void {
		for (const control in this.form.controls) {
			this.form.get(control)!.markAsTouched();
			this.form.get(control)!.updateValueAndValidity({ onlySelf: true });
		}
	}

	verifyEditDelBenefits(
		connBenefits: PositionReqBenefit[],
	): { base_id: string; benefit_id: string }[] {
		const ids: { base_id: string; benefit_id: string }[] = [];

		for (const ben of this.initialEditingBenefits) {
			const cBen = connBenefits.find(
				(b) => b.base_id === ben.base_id && b.benefits_id === ben.benefit_id,
			);

			if (!cBen) {
				ids.push({ base_id: ben.base_id, benefit_id: ben.benefit_id });
			}
		}

		return ids;
	}
}
